import { createId, isoNow, safeFileName, sha256Blob } from './crypto';
import { CURRENT_SCHEMA_VERSION, type AttachmentOwnerType, type StoredAttachment } from './types';

export const DEFAULT_MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

export interface PrepareAttachmentInput {
  ownerType: AttachmentOwnerType;
  ownerId: string;
  deviceId: string;
  fileName?: string;
  capturedAt?: string;
}

export interface PrepareImageOptions {
  maxLongEdge?: number;
  quality?: number;
  maxBytes?: number;
  reencode?: boolean;
}

function ensureAttachmentSize(blob: Blob, maximum: number): void {
  if (!blob.size) throw new Error('附件为空');
  if (blob.size > maximum) {
    throw new Error(`附件过大：${(blob.size / 1024 / 1024).toFixed(1)} MB，上限 ${(maximum / 1024 / 1024).toFixed(0)} MB`);
  }
}

async function imageDimensions(blob: Blob): Promise<{ width?: number; height?: number }> {
  if (typeof createImageBitmap !== 'function') return {};
  const bitmap = await createImageBitmap(blob);
  const dimensions = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return dimensions;
}

async function canvasBlob(
  source: ImageBitmap,
  width: number,
  height: number,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('无法创建图片处理画布');
    context.drawImage(source, 0, 0, width, height);
    return canvas.convertToBlob({ type: mimeType, quality });
  }
  if (typeof document === 'undefined') throw new Error('当前环境不支持图片重编码');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('无法创建图片处理画布');
  context.drawImage(source, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('图片重编码失败')), mimeType, quality);
  });
}

async function normalizeImageBlob(
  original: Blob,
  options: Required<Pick<PrepareImageOptions, 'maxLongEdge' | 'quality'>>,
): Promise<{ blob: Blob; width?: number; height?: number; exifStripped: boolean }> {
  if (typeof createImageBitmap !== 'function') {
    const dimensions = await imageDimensions(original);
    return { blob: original, ...dimensions, exifStripped: false };
  }
  const bitmap = await createImageBitmap(original);
  try {
    const scale = Math.min(1, options.maxLongEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const outputType = original.type === 'image/png' ? 'image/png' : 'image/webp';
    const blob = await canvasBlob(bitmap, width, height, outputType, options.quality);
    return { blob, width, height, exifStripped: true };
  } finally {
    bitmap.close();
  }
}

export async function prepareAttachment(blob: Blob, input: PrepareAttachmentInput): Promise<StoredAttachment> {
  ensureAttachmentSize(blob, DEFAULT_MAX_ATTACHMENT_BYTES);
  const now = isoNow();
  const id = createId('attachment');
  const dimensions = blob.type.startsWith('image/') ? await imageDimensions(blob) : {};
  return {
    id,
    ownerType: input.ownerType,
    ownerId: input.ownerId,
    ownerKey: `${input.ownerType}:${input.ownerId}`,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    fileName: safeFileName(input.fileName ?? `附件-${id}`),
    mimeType: blob.type || 'application/octet-stream',
    byteSize: blob.size,
    sha256: await sha256Blob(blob),
    ...dimensions,
    capturedAt: input.capturedAt,
    createdAt: now,
    updatedAt: now,
    exifStripped: false,
    deviceId: input.deviceId,
    revision: 1,
    blob,
  };
}

export async function prepareImageAttachment(
  file: Blob & { name?: string },
  input: PrepareAttachmentInput,
  options: PrepareImageOptions = {},
): Promise<StoredAttachment> {
  if (!file.type.startsWith('image/')) throw new Error('仅支持图片附件');
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_ATTACHMENT_BYTES;
  ensureAttachmentSize(file, maxBytes);
  const normalized = options.reencode === false
    ? { blob: file as Blob, ...(await imageDimensions(file)), exifStripped: false }
    : await normalizeImageBlob(file, {
      maxLongEdge: options.maxLongEdge ?? 2200,
      quality: options.quality ?? 0.88,
    });
  ensureAttachmentSize(normalized.blob, maxBytes);
  const attachment = await prepareAttachment(normalized.blob, {
    ...input,
    fileName: normalized.blob.type === 'image/webp'
      ? safeFileName(input.fileName ?? file.name ?? '图片').replace(/\.[^.]+$/, '') + '.webp'
      : input.fileName ?? file.name,
  });
  return {
    ...attachment,
    width: normalized.width,
    height: normalized.height,
    exifStripped: normalized.exifStripped,
  };
}
